import { ChartType } from "../components/charts/chart.types";

/**
 * Sunburst / Treemap Types
 */
interface SunburstLeaf {
  name: string;
  value: number;
}

interface SunburstSubNode {
  name: string;
  children: SunburstLeaf[];
}

interface SunburstParentNode {
  name: string;
  children: Array<SunburstSubNode | SunburstLeaf>;
}

interface ApiResponseRow {
  icc_code?: string;
  icc3_code?: string;
  description?: string;
  classification?: string;
  district_name?: string;
  total_cases?: number;
  total_incidence?: number;
  cases_count?: number;
  chemotherapy_count?: number;
  radiation_count?: number;
  surgery_count?: number;
  bone_marrow_transplant_count?: number;
}

interface StageTier {
  stages?: string[];
  cases_count?: number[];
}

interface StagingChartItem {
  tierName: string;
  stage: string;
  value: number;
}

/**
 * Builds a hierarchical tree from the flat API response `data` array
 * for Sunburst and Treemap charts.
 */
function buildSunburstTree(flatData: ApiResponseRow[]): SunburstParentNode[] {
  const root: SunburstParentNode[] = [];

  const map: Record<
    string,
    {
      node: SunburstParentNode;
      childMap: Record<string, SunburstSubNode>;
    }
  > = {};

  flatData.forEach((row) => {
    const description = String(row.description || "").trim();

    if (!description || description.toUpperCase() === "TOTAL") return;

    const parentCode = String(row.icc_code || "").trim();
    const childCode = String(row.icc3_code || "").trim();
    const value = Number(row.total_cases ?? row.cases_count ?? 0);

    if (!parentCode) return;

    // 1. Ensure the parent level (e.g., "I") exists
    if (!map[parentCode]) {
      const newParentNode: SunburstParentNode = {
        name: parentCode,
        children: [],
      };
      map[parentCode] = {
        node: newParentNode,
        childMap: {},
      };
      root.push(newParentNode);
    }

    const parent = map[parentCode];

    // 2. Ensure the sub-parent level (e.g., "IA") exists
    if (childCode && !parent.childMap[childCode]) {
      const newSubParentNode: SunburstSubNode = {
        name: childCode,
        children: [],
      };
      parent.childMap[childCode] = newSubParentNode;
      parent.node.children.push(newSubParentNode);
    }

    // 3. Add the leaf node (e.g., "Leukaemia")
    if (childCode) {
      const subParent = parent.childMap[childCode];
      subParent.children.push({ name: description, value });
    } else {
      parent.node.children.push({ name: description, value });
    }
  });

  return root;
}

const getFlatRows = (data: any): ApiResponseRow[] =>
  (Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : []
  ).filter(
    (row: ApiResponseRow) =>
      row?.icc_code && row?.icc3_code && row?.description,
  );

const isStagingChartData = (data: any): boolean =>
  Boolean(data?.tier_1 || data?.tier_2);

const formatTierName = (tierKey: string): string =>
  tierKey
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getStagingItems = (data: any): StagingChartItem[] =>
  Object.entries(data || {}).flatMap(([tierKey, tierValue]) => {
    const tier = tierValue as StageTier;
    const stages = Array.isArray(tier?.stages) ? tier.stages : [];
    const casesCount = Array.isArray(tier?.cases_count) ? tier.cases_count : [];

    return stages.map((stage, index) => ({
      tierName: formatTierName(tierKey),
      stage,
      value: Number(casesCount[index] || 0),
    }));
  });

/**
 * Main mapper function
 */
export const chartMapper = (
  chartType: ChartType,
  displayType: string,
  chartData: any,
  visualMode = displayType,
) => {
  if (!chartData) return null;

  if (isStagingChartData(chartData)) {
    return mapByStaging(chartType, chartData);
  }

  switch (displayType) {
    case "treatment_details":
      return mapByTreatmentDetails(chartType, chartData);

    case "treatment_types":
      return mapByTreatmentTypes(chartType, chartData, visualMode);

    case "population":
      return mapByPlace(chartType, chartData);

    case "cancer":
      return mapByCancer(chartType, chartData);

    case "aspr":
      return mapByCancer(chartType, chartData);

    default:
      return null;
  }
};

const mapByTreatmentDetails = (chartType: ChartType, data: any) => {
  if (chartType !== "pie") return null;

  return Object.entries(data || {})
    .filter(([, value]) => Number.isFinite(Number(value)))
    .map(([name, value]) => ({
      name,
      value: Number(value),
    }));
};
const treatmentSeries = [
  { name: "Chemotherapy", key: "chemotherapy_count" },
  { name: "Radiation", key: "radiation_count" },
  { name: "Surgery", key: "surgery_count" },
  {
    name: "Bone Marrow Transplant",
    key: "bone_marrow_transplant_count",
  },
] as const;

const mapByTreatmentTypes = (
  chartType: ChartType,
  data: any,
  displayType: string,
) => {
  const rows = Array.isArray(data?.data)
    ? data.data.filter(
        (row: any) => String(row?.icc_code ?? "").toUpperCase() !== "TOTAL",
      )
    : [];
  const isCancerMode = String(displayType ?? "")
    .toLowerCase()
    .trim() === "cancer";
  const categories = rows.map((row: any) =>
    String(
      isCancerMode
        ? row.description ?? row.classification ?? ""
        : row.district_name ?? row.classification ?? "",
    ),
  );

  if (chartType === "bar" && rows.length) {
    return {
      categories,
      axisName: isCancerMode ? "Cancer Names" : "District",
      orientation: "horizontal",
      series: treatmentSeries.map(({ name, key }) => ({
        name,
        type: "bar",
        stack: "treatment-types",
        data: rows.map((row: any) => Number(row[key] ?? 0)),
      })),
    };
  }

  if (chartType === "sunburst") {
    if (isCancerMode) {
      return buildTreatmentCancerSunburst(rows);
    }

    return rows.map((row: any) => ({
      name: String(row?.district_name ?? "Unknown"),
      children: [
        {
          name: "Total Incidence",
          value: Number(row?.total_incidence ?? 0),
        },
      ],
    }));
  }

  if (chartType === "pie") {
    return treatmentSeries.map(({ name }) => ({
      name,
      value: Number(data?.[name] ?? 0),
    }));
  }

  return null;
};

const buildTreatmentCancerSunburst = (rows: any[]) => {
  const parentNodes: Record<string, SunburstParentNode> = {};

  rows.forEach((row) => {
    const parentName = String(row?.icc_code ?? "").trim();
    const childName = String(row?.icc3_code ?? row?.classification ?? "").trim();

    if (!parentName || !childName) return;

    if (!parentNodes[parentName]) {
      parentNodes[parentName] = {
        name: parentName,
        children: [],
      };
    }

    const treatmentChildren = treatmentSeries.map(({ name, key }) => ({
      name,
      value: Number(row?.[key] ?? 0),
    }));

    parentNodes[parentName].children.push({
      name: childName,
      children: [
        {
          name: "Total Incidence",
          value: Number(row?.total_incidence ?? 0),
        },
        ...treatmentChildren,
      ],
    });
  });

  return Object.values(parentNodes);
};

const mapByPlace = (chartType: ChartType, data: any) => {
  const districtNames = Array.isArray(data?.district_names)
    ? data.district_names
    : [];
  const casesCount = Array.isArray(data?.cases_count) ? data.cases_count : [];

  switch (chartType) {
    case "bar":
      return {
        categories: districtNames,
        values: casesCount,
      };

    case "pie":
      return districtNames.map((district: string, index: number) => ({
        name: district,
        value: casesCount[index],
      }));

    case "sunburst":
      return getFlatRows(data).length
        ? buildSunburstTree(getFlatRows(data))
        : districtNames.map((district: string, index: number) => ({
            name: district,
            value: casesCount[index],
          }));

    default:
      return null;
  }
};

const mapByCancer = (chartType: ChartType, data: any) => {
  const cancerNames = Array.isArray(data?.cancer_names)
    ? data.cancer_names
    : [];
  const casesCount = Array.isArray(data?.cases_count) ? data.cases_count : [];
  const population = Array.isArray(data?.population) ? data.population : [];

  switch (chartType) {
    case "bar":
      return {
        categories: cancerNames,
        values: casesCount,
      };

    case "pie":
      return cancerNames.map((name: string, index: number) => ({
        name,
        value: casesCount[index],
        population: population[index],
      }));

    case "sunburst":
      return getFlatRows(data).length
        ? buildSunburstTree(getFlatRows(data))
        : cancerNames.map((name: string, index: number) => ({
            name,
            value: casesCount[index],
          }));

    default:
      return null;
  }
};

const mapByStaging = (chartType: ChartType, data: any) => {
  const stagingItems = getStagingItems(data);

  switch (chartType) {
    case "bar":
      return {
        categories: stagingItems.map(
          (item) => `${item.tierName} - ${item.stage}`,
        ),
        values: stagingItems.map((item) => item.value),
      };

    case "pie":
      return stagingItems.map((item) => ({
        name: `${item.tierName} - ${item.stage}`,
        value: item.value,
      }));

    case "sunburst":
      return Object.entries(data || {}).map(([tierKey, tierValue]) => {
        const tier = tierValue as StageTier;
        const stages = Array.isArray(tier?.stages) ? tier.stages : [];
        const casesCount = Array.isArray(tier?.cases_count)
          ? tier.cases_count
          : [];

        return {
          name: formatTierName(tierKey),
          children: stages.map((stage, index) => ({
            name: stage,
            value: Number(casesCount[index] || 0),
          })),
        };
      });

    default:
      return null;
  }
};
